/* Control de EPP: catálogo de ítems y depósitos con sus responsables (solo SSHO).
 *
 * Nada se borra: ítems y depósitos se dan de baja. Cambiar la vida útil de un
 * ítem no mueve las fechas de reposición de las entregas ya registradas. Un
 * depósito con stock no se puede dar de baja (primero se transfiere). */
import { IonButton, IonContent, IonIcon, IonLabel, IonPage, IonSegment, IonSegmentButton } from "@ionic/react";
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from "@material-ui/core";
import { addOutline, createOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import Header from "src/components/Header";
import { BuscadorFuncionario, Cargando, useToastEpp } from "src/components/epp/EppComunes";
import EppAPI, { EPP_ROL_KEY, Deposito, Item, ItemPayload, Responsable } from "src/services/epp";
import "./Epp.css";

const ITEM_VACIO: ItemPayload = { Nombre: "", Tipo: "EPP", VidaUtilDias: null, Criterio: "", UsaTalle: false, Activo: true };

interface DepositoForm {
  Nombre: string;
  Activo: boolean;
  Responsables: Responsable[];
}

const EppAdmin: React.FC = () => {
  const toast = useToastEpp();
  const [vista, setVista] = useState<"catalogo" | "depositos">("catalogo");
  const [items, setItems] = useState<Item[] | null>(null);
  const [depositos, setDepositos] = useState<Deposito[] | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [verInactivos, setVerInactivos] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [itemEditando, setItemEditando] = useState<Item | null>(null);
  const [itemForm, setItemForm] = useState<ItemPayload | null>(null);
  const [vida, setVida] = useState("");

  const [depEditando, setDepEditando] = useState<Deposito | null>(null);
  const [depForm, setDepForm] = useState<DepositoForm | null>(null);

  const cargar = async () => {
    try {
      const [i, d] = await Promise.all([EppAPI.items(true), EppAPI.depositos()]);
      setItems(i);
      setDepositos(d);
    } catch (err) {
      setItems([]);
      setDepositos([]);
      toast.error(err, "No se pudo cargar la configuración");
    }
  };

  useEffect(() => {
    cargar();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const itemsVisibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (items || []).filter((i) => (verInactivos || i.Activo) && (!q || i.Nombre.toLowerCase().includes(q)));
  }, [items, busqueda, verInactivos]);

  const abrirItem = (item: Item | null) => {
    setItemEditando(item);
    setItemForm(item ? { ...item, Criterio: item.Criterio || "" } : ITEM_VACIO);
    setVida(item?.VidaUtilDias ? String(item.VidaUtilDias) : "");
  };

  const guardarItem = async () => {
    if (!itemForm) return;
    if (!itemForm.Nombre.trim()) return toast.aviso("El nombre es obligatorio");
    const dias = vida.trim() ? Number(vida) : null;
    if (dias !== null && (!Number.isInteger(dias) || dias <= 0)) return toast.aviso("La vida útil debe ser un número de días");
    setGuardando(true);
    try {
      const payload = { ...itemForm, VidaUtilDias: dias };
      if (itemEditando) await EppAPI.actualizarItem(itemEditando.Id, payload);
      else await EppAPI.crearItem(payload);
      toast.ok(itemEditando ? "Ítem actualizado" : "Ítem creado");
      setItemForm(null);
      cargar();
    } catch (err) {
      toast.error(err, "No se pudo guardar el ítem");
    } finally {
      setGuardando(false);
    }
  };

  const abrirDeposito = (d: Deposito | null) => {
    setDepEditando(d);
    setDepForm(d ? { Nombre: d.Nombre, Activo: d.Activo, Responsables: d.Responsables } : { Nombre: "", Activo: true, Responsables: [] });
  };

  const guardarDeposito = async () => {
    if (!depForm) return;
    if (!depForm.Nombre.trim()) return toast.aviso("El nombre es obligatorio");
    setGuardando(true);
    try {
      const payload = { Nombre: depForm.Nombre, Activo: depForm.Activo, Responsables: depForm.Responsables.map((r) => r.cedula) };
      if (depEditando) await EppAPI.actualizarDeposito(depEditando.Id, payload);
      else await EppAPI.crearDeposito(payload);
      toast.ok(depEditando ? "Depósito actualizado" : "Depósito creado");
      setDepForm(null);
      // Cambian los depósitos que opera el usuario: se vuelve a pedir el rol.
      try { sessionStorage.removeItem(EPP_ROL_KEY); } catch { /* ignore */ }
      EppAPI.miRol(true);
      cargar();
    } catch (err) {
      toast.error(err, "No se pudo guardar el depósito");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo="Catálogo y depósitos de EPP" />
      <IonContent id="epp-admin">
        <div className="epp-page campos-mui">
          <IonSegment value={vista} onIonChange={(e) => setVista((e.detail.value as any) || "catalogo")}>
            <IonSegmentButton value="catalogo"><IonLabel>Catálogo</IonLabel></IonSegmentButton>
            <IonSegmentButton value="depositos"><IonLabel>Depósitos</IonLabel></IonSegmentButton>
          </IonSegment>

          {vista === "catalogo" && (
            <>
              <div className="epp-barra" style={{ marginTop: 12 }}>
                <TextField variant="outlined" size="small" placeholder="Buscar ítem" value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)} style={{ flex: "1 1 200px" }} />
                <IonButton onClick={() => abrirItem(null)}>
                  <IonIcon slot="start" icon={addOutline} /> Nuevo ítem
                </IonButton>
              </div>
              <FormControlLabel
                control={<Switch color="primary" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} />}
                label="Mostrar dados de baja"
              />
              {items === null ? <Cargando /> : (
                <div className="epp-card">
                  {itemsVisibles.length === 0 && <div className="epp-vacio">Ningún ítem coincide.</div>}
                  {itemsVisibles.map((i) => (
                    <div key={i.Id} className="epp-fila" style={{ opacity: i.Activo ? 1 : 0.6 }}>
                      <div className="epp-fila-cabecera">
                        <span className="epp-item">{i.Nombre}</span>
                        <IonButton size="small" fill="clear" style={{ margin: "-6px -8px -6px 0" }} onClick={() => abrirItem(i)}>
                          <IonIcon slot="icon-only" icon={createOutline} />
                        </IonButton>
                      </div>
                      <div className="epp-meta">
                        <span>{i.Tipo}</span>
                        <span>{i.VidaUtilDias ? `${i.VidaUtilDias} días` : "Definir duración"}</span>
                        {i.UsaTalle && <span>Con talle</span>}
                        {!i.Activo && <span>Dado de baja</span>}
                      </div>
                      {i.Criterio && <div className="epp-meta">{i.Criterio}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {vista === "depositos" && (
            <>
              <div className="epp-barra" style={{ marginTop: 12 }}>
                <IonButton onClick={() => abrirDeposito(null)}>
                  <IonIcon slot="start" icon={addOutline} /> Nuevo depósito
                </IonButton>
              </div>
              {depositos === null ? <Cargando /> : depositos.map((d) => (
                <div key={d.Id} className="epp-card" style={{ opacity: d.Activo ? 1 : 0.6 }}>
                  <div className="epp-fila-cabecera">
                    <span className="epp-card-titulo">{d.Nombre}</span>
                    <IonButton size="small" fill="clear" style={{ margin: "-6px -8px -6px 0" }} onClick={() => abrirDeposito(d)}>
                      <IonIcon slot="icon-only" icon={createOutline} />
                    </IonButton>
                  </div>
                  <div className="epp-meta">
                    {d.Responsables.length
                      ? d.Responsables.map((r) => <span key={r.cedula}>{r.nombre}</span>)
                      : <span>Sin responsables (solo SSHO)</span>}
                    {!d.Activo && <span>Dado de baja</span>}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <Dialog className="epp-dialogo" open={itemForm !== null} onClose={() => !guardando && setItemForm(null)} fullWidth maxWidth="sm">
          <DialogTitle>{itemEditando ? "Editar ítem" : "Nuevo ítem"}</DialogTitle>
          {itemForm && (
            <DialogContent className="campos-mui">
              <TextField label="Nombre" variant="outlined" fullWidth margin="dense" value={itemForm.Nombre}
                inputProps={{ maxLength: 150 }} onChange={(e) => setItemForm({ ...itemForm, Nombre: e.target.value })} />
              <TextField select label="Tipo" variant="outlined" fullWidth margin="dense" value={itemForm.Tipo}
                onChange={(e) => setItemForm({ ...itemForm, Tipo: e.target.value as "EPP" | "EPI" })}>
                <MenuItem value="EPP">EPP</MenuItem>
                <MenuItem value="EPI">EPI</MenuItem>
              </TextField>
              <TextField label="Vida útil estimada (días)" type="number" variant="outlined" fullWidth margin="dense"
                value={vida} onChange={(e) => setVida(e.target.value)}
                helperText="Vacío = 'Definir duración'. Ajustala a la ficha técnica y a las condiciones reales de uso." />
              <TextField label="Criterio / observación" variant="outlined" fullWidth margin="dense" multiline
                value={itemForm.Criterio || ""} inputProps={{ maxLength: 500 }}
                onChange={(e) => setItemForm({ ...itemForm, Criterio: e.target.value })} />
              <FormControlLabel
                control={<Switch color="primary" checked={itemForm.UsaTalle}
                  onChange={(e) => setItemForm({ ...itemForm, UsaTalle: e.target.checked })} />}
                label="Se controla por talle"
              />
              <FormControlLabel
                control={<Switch color="primary" checked={itemForm.Activo}
                  onChange={(e) => setItemForm({ ...itemForm, Activo: e.target.checked })} />}
                label="Activo"
              />
            </DialogContent>
          )}
          <DialogActions>
            <IonButton fill="clear" onClick={() => setItemForm(null)} disabled={guardando}>Cancelar</IonButton>
            <IonButton onClick={guardarItem} disabled={guardando}>{guardando ? "Guardando..." : "Guardar"}</IonButton>
          </DialogActions>
        </Dialog>

        <Dialog className="epp-dialogo" open={depForm !== null} onClose={() => !guardando && setDepForm(null)} fullWidth maxWidth="sm">
          <DialogTitle>{depEditando ? "Editar depósito" : "Nuevo depósito"}</DialogTitle>
          {depForm && (
            <DialogContent className="campos-mui">
              <TextField label="Nombre" variant="outlined" fullWidth margin="dense" value={depForm.Nombre}
                inputProps={{ maxLength: 150 }} onChange={(e) => setDepForm({ ...depForm, Nombre: e.target.value })} />
              <p className="epp-meta">Responsables: cargan stock, transfieren y entregan desde este depósito.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {depForm.Responsables.map((r) => (
                  <Chip key={r.cedula} label={r.nombre}
                    onDelete={() => setDepForm({ ...depForm, Responsables: depForm.Responsables.filter((x) => x.cedula !== r.cedula) })} />
                ))}
              </div>
              <BuscadorFuncionario
                key={depForm.Responsables.length}
                label="Agregar responsable"
                onSelect={(f) =>
                  f && !depForm.Responsables.some((r) => r.cedula === f.cedula) &&
                  setDepForm({ ...depForm, Responsables: [...depForm.Responsables, { cedula: f.cedula, nombre: f.nombre }] })
                }
              />
              <FormControlLabel
                control={<Switch color="primary" checked={depForm.Activo}
                  onChange={(e) => setDepForm({ ...depForm, Activo: e.target.checked })} />}
                label="Activo"
              />
            </DialogContent>
          )}
          <DialogActions>
            <IonButton fill="clear" onClick={() => setDepForm(null)} disabled={guardando}>Cancelar</IonButton>
            <IonButton onClick={guardarDeposito} disabled={guardando}>{guardando ? "Guardando..." : "Guardar"}</IonButton>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default EppAdmin;
